import { ApiError } from '@l2u/web-shared';

// What to do when GET /api/auth/admin/me fails.
//
// Extracted from AdminAuthProvider.loadUser for the same reason
// redeemCredentials was extracted from the two screens that used it: apps/admin
// has no jsdom, so a component test cannot reach the decision, and this one had
// been silently destroying good sessions.
//
// The old rule was "401 OR 403 -> auth.signOut()", written for a real case —
// a Supabase user with no matching public.users row is not an admin and should
// not sit on a half-signed-in screen. Two things were wrong with it:
//
//  1. It included 401, which is not "you are not an admin" but "the API could
//     not validate your token". That is frequently a SERVER-side condition. It
//     was exactly that for every tenant on a dedicated cell: the API validated
//     bearer tokens against the shared project (see
//     apps/api/src/middleware/bearerAuth.ts) and 401'd a session the cell had
//     just minted. The SPA answered by deleting
//     it — so a password-reset link redeemed fine, the form opened, and
//     updateUser() then reported "Auth session missing", blaming the password
//     the user had just typed for a fault two layers away.
//
//  2. It used the DEFAULT scope. supabase-js defaults signOut() to
//     `scope: 'global'`, which revokes EVERY refresh token the account holds —
//     so one failed request on one device signed the user out everywhere.
//
// Keeping the session on a 401 does not strand anyone: the caller still clears
// `user`, and the shell routes a signed-in-but-not-loaded admin to the login
// screen the same way it always did.

/**
 * The part of supabase-js's auth client this needs — structural, so a plain
 * object can stand in (apps/admin has no jsdom).
 */
export type SignOutLike = { signOut(options: { scope: 'local' }): Promise<unknown> };

export async function handleMeFailure(
  auth: SignOutLike,
  err: unknown,
): Promise<'signed-out' | 'kept'> {
  if (!(err instanceof ApiError) || err.status !== 403) return 'kept';
  try {
    await auth.signOut({ scope: 'local' });
  } catch {
    // Revoking is best-effort; the caller must still drop local state, or the
    // UI would believe a rejected account is still signed in.
  }
  return 'signed-out';
}
